"use client";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { useLocale } from "next-intl";
import { z } from "zod";
import { useMemo } from "react";
import DynamicForm from "@/components/dynamicForm";
import { useRouter } from "@/i18n/navigation";
import { ProductType, TrackingEvent, useAnalyticsContext } from "@/analytics";

// Utils
import { generatePnrFaqUrl } from "@/utils/bus/generatePnrUrl";
import { getLocalizedPath } from "@/utils/pathUtils";

const PnrForm = ({ onClose }) => {
  const { dispatch: trackingDispatch } = useAnalyticsContext();
  const router = useRouter();
  const { locale } = useLocale();
  const t = useTranslationDetector("bus");
  const schema = useMemo(() => (
    z.object({
      pnrCode: z
        .string()
        .trim()
        .min(1, t("pnrFormPnrCodeRequired"))
        .max(20, t("pnrFormPnrCodeTooLong")),
      productType: z
        .string()
        .optional(),
      surname: z
        .string()
        .trim()
        .min(1, t("pnrFormSurnameRequired"))
        .max(50, t("pnrFormSurnameTooLong")),
    })
  ), [t]);

  const reservationUrl = getLocalizedPath('/reservation/[hashCode]', locale);

  return (
    <div className="pb-8">

      <DynamicForm
        mode="onSubmit"
        schema={schema}
        dryRun={false}
        endpoint="/api/reservation/pnr"
        defaultValues={{ productType: "Bus", pnrCode: "", surname: "" }}
        onSuccess={(res) => {
          trackingDispatch({
            type: TrackingEvent.CHECK_RESERVATION,
            payload: {
              productType: ProductType.BUS,
              pnr_number: res?.pnr_number ?? res?.pnrCode ?? "",
              reservation_category: "bus",
            },
          });
          const url = reservationUrl.replace('[hashCode]', res?.code || '');
          router.push(url);
          onClose();
        }}
        onError={(msg) => {
          console.warn("PNR Search error:", msg);
        }}
        className="space-y-4"
      >
        <DynamicForm.Error customErrorMessage={t("pnrFormErrorMessage")} className="rounded bg-red-50 px-3 py-2 text-sm text-red-700" />

        <DynamicForm.Field
          name="pnrCode"
          type="text"
          label={t("pnrFormPnrCode")}
        />

        <div className="hidden">
          <DynamicForm.Field
            name="productType"
            type="hidden"
          />
        </div>

        <DynamicForm.Field
          name="surname"
          type="text"
          label={t("pnrFormSurname")}
        />

        <div className="flex flex-col gap-2">
          <DynamicForm.Button spanClassName="h-[50px] flex items-center justify-center text-center font-medium" className="flex-1 rounded-lg bg-primary px-4 text-white hover:bg-primary/90">
            {t("pnrFormSubmit")}
          </DynamicForm.Button>

          <div className="mt-1 text-[16px] inline" style={{ color: "#585B5E" }}>
            {t("pnrFormHelpText")}{" "}
            <button
              type="button"
              className="text-[#01007F] underline p-0 m-0 bg-transparent border-0 cursor-pointer font-inherit text-[16px]"
              style={{ verticalAlign: "baseline", display: "contents" }}
              onClick={() => {
                // TODO: Yardım bağlantısı/aksiyonu
                generatePnrFaqUrl(locale);
              }}
            >
              {t("pnrFormCantFindLink")}
            </button>
          </div>

        </div>
      </DynamicForm>
    </div>
  );
};

export default PnrForm;
